import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import api from '../../utils/api';
import { PartnerEndpoints } from '../../utils/endpoints';
import AuthLayout from './AuthLayout';
import { Alert, Button, FormField } from '../ui';

const TwoFactorVerify = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { tempToken, isFirstLogin, setAuthData, setTempToken, authData } = useAuth();
  const navigate = useNavigate();

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post(
        PartnerEndpoints.twoFactorVerify,
        { code },
        // The request interceptor only carries the session token; 2FA runs on the temporary one.
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );

      const { token, partner } = response.data.data;
      const verifiedAuthData = { username: partner.name ?? partner.enterprise?.name, token };

      setAuthData(verifiedAuthData);
      localStorage.setItem('authData', JSON.stringify(verifiedAuthData));
      setTempToken(null);

      navigate(isFirstLogin ? '/first-login' : '/layout');
    } catch (err) {
      if (err.response?.status === 422) {
        const msg = err.response.data?.errors?.code?.[0];
        if (msg?.includes('not yet activated')) {
          navigate('/2fa/setup');
        } else {
          setError('Invalid code. Please try again.');
        }
      } else if (err.response?.status === 403) {
        setError('Session expired. Please log in again.');
        navigate('/');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!tempToken && !authData?.token) return <Navigate to="/" replace />;

  return (
    <AuthLayout
      title="Two-factor authentication"
      subtitle="Enter the 6-digit code from your authenticator app."
    >
      <form onSubmit={handleVerify}>
        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        <FormField label="Authentication code" htmlFor="code" required>
          <input
            type="text"
            id="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="form-control text-center text-f-h5 tracking-widest"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            required
          />
        </FormField>

        <Button type="submit" size="lg" loading={loading} disabled={code.length !== 6} className="w-full">
          Verify
        </Button>
      </form>
    </AuthLayout>
  );
};

export default TwoFactorVerify;
